"use client";

import { useEffect, useRef, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import supabaseAuthService, { NETWORK_BLOCKED_MESSAGE } from '../services/supabaseAuthService';
import { getSupabaseClient } from '../services/supabaseClient';
import { getApiUrl } from '../services/config';
import { isDemoMode } from "@/app/_module/utils/demoMode";
import { removeRealtimeChannel } from "../services/realtimeTeardown";

// Session tab ID storage key
const SESSION_TAB_ID_KEY = 'insuracentral_active_tab_id';
const SESSION_HEARTBEAT_KEY = 'insuracentral_session_heartbeat';
const SESSION_ACTIVE_FLAG_KEY = 'insuracentral_session_was_active';
const SESSION_LOGGED_OUT_KEY = 'insuracentral_logged_out';
const SESSION_OFFLINE_BEACON_KEY = 'insuracentral_offline_beacon';
// Set (sessionStorage, per-tab) when the user clicks "Use InsuraCentral here" on the
// duplicate-tab blocker — lets this tab claim ownership on the reload that follows
const SESSION_TAB_TAKEOVER_KEY = 'insuracentral_tab_takeover';
// Breadcrumb a blocked duplicate tab drops (localStorage) so the MAIN tab can rib
// the agent about it — carries zero session state
const SESSION_DUPLICATE_ATTEMPT_KEY = 'insuracentral_duplicate_tab_attempt';

// Rotating call-outs shown on the main tab when a second tab gets blocked
const DUPLICATE_TAB_ROASTS = [
  "👀 Caught you opening a second tab. One tab at a time, champ — it's been benched.",
  "🚫 Two tabs? Bold move. The other one's in timeout now.",
  "🕵️ A second tab tried to sneak in. We handled it. Carry on.",
  "😅 Nice try — this is a one-tab relationship. The other tab knows.",
];

// Shared with useAutoStatusManager — set when a call/dialer session is active
const ACTIVE_CALL_FLAG_KEY = 'insuracentral_active_call';

/** Returns true if the user is currently on a call or in a dialer session (checks ALL tabs) */
const isUserOnActiveCall = (): boolean => {
  try {
    return sessionStorage.getItem(ACTIVE_CALL_FLAG_KEY) === 'true'
      || localStorage.getItem(ACTIVE_CALL_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * SessionValidator Component
 *
 * Enforces strict single-session/single-tab policy:
 * - Only ONE browser tab can be active at a time
 * - Closing the tab ends the session (requires re-login with MFA)
 * - No bypassing by typing URLs directly
 *
 * Features:
 * - TAB UNIQUENESS: Only one tab allowed per session
 * - CLOSE TAB = LOGOUT: Uses beforeunload + heartbeat to detect tab close
 * - REAL-TIME detection via Supabase Realtime broadcast
 * - Validates session on every page load
 */
export function SessionValidator() {
  // Sandbox: single-session enforcement is deliberately disabled (pooled seats can be
  // open in more than one place), so this validator has nothing to enforce — and its
  // redirects would bounce a visitor to a login screen they have no password for.
  if (isDemoMode()) return null;

  const router = useRouter();
  const [showKickedOutModal, setShowKickedOutModal] = useState(false);
  const [kickedOutMessage, setKickedOutMessage] = useState('');
  const [showDuplicateTabBlocker, setShowDuplicateTabBlocker] = useState(false);
  // Center-screen roast shown on the MAIN tab when a duplicate tab gets blocked
  const [duplicateRoast, setDuplicateRoast] = useState<string | null>(null);
  const roastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  // When this tab claimed heartbeat ownership — newer claim wins ("use here instead")
  const claimedAtRef = useRef<number>(0);
  const supabaseChannelRef = useRef<any>(null);
  const hasTriggeredLogout = useRef(false);
  const currentSessionIdRef = useRef<string | null>(null);
  // ⚠️ Seeded to mount time, NOT 0. At 0 the "only validate every 5 minutes"
  // guard below computes Date.now() - 0 — about 1.7 trillion ms — so it never
  // blocked, and the visibilitychange check ran at the worst possible moment:
  // immediately after login, while Supabase was still rehydrating. A desktop tab
  // rarely fires visibilitychange right after login; a WebView foregrounding
  // always does.
  const lastValidationRef = useRef<number>(Date.now());
  const tabIdRef = useRef<string>('');
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityCleanupRef = useRef<(() => void) | null>(null);
  const accessTokenRef = useRef<string | null>(null);

  // Handle session invalidation (called by realtime)
  const handleSessionInvalidation = useCallback((message: string, reason = 'logged_in_elsewhere') => {
    if (hasTriggeredLogout.current) return; // Prevent multiple triggers
    hasTriggeredLogout.current = true;

    setKickedOutMessage(message);
    setShowKickedOutModal(true);

    // Clear local session after showing the modal
    setTimeout(async () => {
      try {
        // LOCAL scope: this device was evicted because the user logged in on a
        // NEW device — a global signOut here would revoke the new device's
        // tokens too and ping-pong both sessions dead.
        await supabaseAuthService.signOut({ scope: 'local' });
      } catch (e) {
        // Ignore signout errors
      }
      router.push(`/auth/sign-in?reason=${reason}`);
    }, 3000); // Show modal for 3 seconds before redirecting
  }, [router]);

  // Ref for callback to avoid stale closures in Supabase listener
  const handleSessionInvalidationRef = useRef(handleSessionInvalidation);
  useEffect(() => {
    handleSessionInvalidationRef.current = handleSessionInvalidation;
  }, [handleSessionInvalidation]);

  // Ask the backend whether THIS supabase session still owns the active
  // user_sessions row, or was replaced by a login elsewhere. This is the
  // fallback for the one-shot realtime broadcast — a device that misses the
  // broadcast (sleeping laptop, backgrounded tab) gets evicted here instead
  // of staying logged in forever.
  // FAIL-OPEN: network hiccups / server errors must never log an agent out.
  const validateSessionWithBackend = useCallback(async (): Promise<boolean> => {
    try {
      const session = await supabaseAuthService.getCurrentSession();

      if (!session?.access_token) {
        return true; // no session to judge — other paths handle missing sessions
      }

      const response = await fetch(getApiUrl('auth/validate-session'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      }).catch(() => null);

      if (!response) return true; // network error — fail open

      // Explicit auth rejection = session dead
      if (response.status === 401 || response.status === 403) {
        // Banned network: say so. The callers' "another device" message is then a no-op (already triggered).
        const body = await response.json().catch(() => null);
        if (body?.error === 'NETWORK_BLOCKED') {
          handleSessionInvalidationRef.current(NETWORK_BLOCKED_MESSAGE, 'network_blocked');
        }
        return false;
      }

      if (!response.ok) return true; // 5xx etc — fail open

      const data = await response.json().catch(() => null);
      if (data && data.valid === false) {
        return false; // backend says this session was replaced by a login elsewhere
      }

      return true;
    } catch (error) {
      console.error('[SessionValidator] Error validating session:', error);
      return true; // fail open
    }
  }, []);

  // Generate unique tab ID
  const generateTabId = useCallback(() => {
    return `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }, []);

  // Clear session completely (for tab close / logout)
  const clearSessionCompletely = useCallback(async () => {
    try {
      // Clear tab tracking
      sessionStorage.removeItem(SESSION_TAB_ID_KEY);
      localStorage.removeItem(SESSION_HEARTBEAT_KEY);
      localStorage.removeItem(SESSION_ACTIVE_FLAG_KEY);

      // Sign out from Supabase — LOCAL scope: every caller of this function is
      // cleaning up THIS device's dead/stale session (tab closed, already logged
      // out, refresh failed). Deliberate cross-device eviction is backend-driven.
      await supabaseAuthService.signOut({ scope: 'local' });

      // Clear any other session data
      sessionStorage.clear();
    } catch (error) {
      console.error('[SessionValidator] Error clearing session:', error);
    }
  }, []);

  // Check if this is a fresh browser session (tab was closed previously)
  const isFreshBrowserSession = useCallback(() => {
    // sessionStorage is tab-specific and persists across page refresh within same tab
    // This is the MOST RELIABLE way to detect if this is a refresh vs new tab
    const currentStoredTabId = sessionStorage.getItem(SESSION_TAB_ID_KEY);
    const lastHeartbeat = localStorage.getItem(SESSION_HEARTBEAT_KEY);
    const sessionWasActive = localStorage.getItem(SESSION_ACTIVE_FLAG_KEY);
    const wasLoggedOut = localStorage.getItem(SESSION_LOGGED_OUT_KEY);

    // CRITICAL: If user explicitly logged out, require fresh login
    // This prevents opening new tab after logout from accessing dashboard
    if (wasLoggedOut === 'true' && !currentStoredTabId) {
      return 'user_logged_out';
    }

    // KEY CHECK: If we have a tab ID in sessionStorage, this is definitely a page refresh
    // sessionStorage is cleared when tab is closed, so having it = same tab
    if (currentStoredTabId) {
      return 'same_tab';
    }

    // No tab ID in sessionStorage means either:
    // 1. Brand new browser/tab (first login)
    // 2. User closed tab and opened new one (should require re-login)
    // 3. User opened second tab while first is running (duplicate)

    if (!lastHeartbeat) {
      // No heartbeat at all
      if (sessionWasActive === 'true') {
        // Session was active before but heartbeat is gone = tab was closed
        return 'tab_was_closed';
      }
      // No previous session - first login
      return 'first_login';
    }

    // We have heartbeat but no sessionStorage tab ID
    // Parse heartbeat to check freshness
    const heartbeatData = JSON.parse(lastHeartbeat);
    const timeSinceLastBeat = Date.now() - heartbeatData.timestamp;

    // Smart staleness detection:
    // - If the tab was HIDDEN (user tabbed to carrier site, closed laptop), browsers throttle
    //   setInterval to ~1x/minute. Use a generous 5-minute threshold.
    // - If the tab was VISIBLE but heartbeat is stale, it was truly closed (interval stopped).
    //   Use a shorter 60-second threshold (still generous to avoid false positives).
    const wasHidden = heartbeatData.state === 'hidden';
    const staleThreshold = wasHidden ? 5 * 60 * 1000 : 60 * 1000; // 5 min hidden, 60s visible

    if (timeSinceLastBeat > staleThreshold) {
      return 'tab_was_closed';
    }

    // Heartbeat is fresh but we have no sessionStorage tab ID:
    // another tab is running RIGHT NOW and this is a second tab being opened.
    // SINGLE-TAB POLICY: block it (blocker UI offers a "use here instead" takeover).
    return 'duplicate_tab';
  }, []);

  // Initial session validation on mount with tab enforcement
  useEffect(() => {
    let mounted = true;

    const validateOnMount = async () => {
      // Skip if already triggered logout
      if (hasTriggeredLogout.current) return;

      setIsValidating(true);

      // FIRST: Check heartbeat/tab state BEFORE checking Supabase session
      // This is critical because Supabase session may still be loading
      const freshSessionCheck = isFreshBrowserSession();

      if (freshSessionCheck === 'tab_was_closed') {
        // Previous tab was closed - force re-login regardless of Supabase state
        if (mounted && !hasTriggeredLogout.current) {
          hasTriggeredLogout.current = true;
          localStorage.removeItem(SESSION_ACTIVE_FLAG_KEY);
          await clearSessionCompletely();
          router.push('/auth/sign-in?reason=tab_closed');
        }
        return;
      } else if (freshSessionCheck === 'user_logged_out') {
        // User explicitly logged out - force re-login regardless of Supabase state
        if (mounted && !hasTriggeredLogout.current) {
          hasTriggeredLogout.current = true;
          await clearSessionCompletely();
          router.push('/auth/sign-in?reason=logged_out');
        }
        return;
      } else if (freshSessionCheck === 'duplicate_tab') {
        // SINGLE-TAB POLICY: another tab is actively running this session.
        // Unless the user explicitly clicked "Use InsuraCentral here" (takeover flag),
        // block THIS tab. Critically: do NOT sign out, do NOT write a tab ID, do NOT
        // start a heartbeat — the original tab keeps working untouched.
        if (sessionStorage.getItem(SESSION_TAB_TAKEOVER_KEY) === 'true') {
          // User chose to move here — claim ownership below (newer claimedAt wins;
          // the old tab sees it via the storage listener and stands down).
          sessionStorage.removeItem(SESSION_TAB_TAKEOVER_KEY);
        } else {
          // Tell the main tab a duplicate got blocked (it shows a playful toast).
          // Harmless breadcrumb — no session state involved.
          try { localStorage.setItem(SESSION_DUPLICATE_ATTEMPT_KEY, Date.now().toString()); } catch {}
          // Try to close the duplicate tab outright. Browsers only honor this for
          // script-opened tabs — for user-opened tabs (Cmd+T, typed URL) it's a
          // silent no-op, so the blocker below is the guaranteed fallback.
          try { window.close(); } catch {}
          if (mounted) {
            setShowDuplicateTabBlocker(true);
            setIsValidating(false);
          }
          return;
        }
      }

      // For 'first_login' or 'same_tab', now check Supabase session
      // Give Supabase a moment to initialize from storage on page refresh
      let session = await supabaseAuthService.getCurrentSession();

      // ⚠️ RETRY ON EVERY PATH, not just 'same_tab'.
      //
      // Supabase rehydrates its session from storage ASYNCHRONOUSLY, so a read
      // taken too early returns null for a session that is perfectly valid. The
      // retry used to be limited to 'same_tab', which meant 'first_login' got a
      // single shot — and in the iOS WKWebView that shot lands before storage is
      // ready. Result: sign in, 2FA, dashboard renders for a beat, then
      // "Your session has expired" and back to the login screen. Reproduced in
      // the simulator 2026-08-25; the backend session row was ACTIVE and
      // mfa_satisfied the whole time, so this was purely a client-side false
      // negative.
      //
      // Retrying cannot weaken anything: if there is genuinely no session, the
      // second read returns null too and the logout below still happens.
      if (!session) {
        await new Promise(resolve => setTimeout(resolve, 500));
        session = await supabaseAuthService.getCurrentSession();
        if (!session) {
          // One more, longer, for a cold WebView start.
          await new Promise(resolve => setTimeout(resolve, 1000));
          session = await supabaseAuthService.getCurrentSession();
        }
      }

      if (!session) {
        // Same rule as the refresh path below: no session because we could not
        // REACH the auth server is not proof the session is gone. Reached here
        // only when the stored token is already expired (otherwise
        // getCurrentSession's localStorage fallback returns it), which is
        // precisely the window where an outage looks like an expiry.
        if (supabaseAuthService.lastSessionReadWasTransportFailure()) {
          console.warn(
            '[SessionValidator] Could not reach the auth server to read the session — keeping it, will retry.',
          );
          return;
        }

        if (mounted && !hasTriggeredLogout.current) {
          hasTriggeredLogout.current = true;
          // Clear any stale data
          localStorage.removeItem(SESSION_HEARTBEAT_KEY);
          localStorage.removeItem(SESSION_ACTIVE_FLAG_KEY);
          sessionStorage.removeItem(SESSION_TAB_ID_KEY);
          router.push('/auth/sign-in?reason=session_expired');
        }
        return;
      }

      // Session exists and heartbeat is valid ('first_login' or 'same_tab')
      // Check if the session token is expired
      if (session.expires_at) {
        const expiresAt = session.expires_at * 1000; // Convert to milliseconds
        const now = Date.now();

        if (now >= expiresAt) {
          // Try to refresh the session
          const refreshedSession = await supabaseAuthService.refreshSession();

          if (!refreshedSession) {
            // COULD NOT REACH THE AUTH SERVER != SESSION IS DEAD.
            //
            // refreshSession() returns null both when the token is genuinely
            // rejected AND when the request never got an answer (offline, DNS,
            // timeout, 5xx, a pooler refusing connections). Signing out on the
            // second case is what turned a ~2 minute Supabase pooler outage on
            // 2026-08-28 (16:36-16:37 UTC) into ~30 agents booted mid-shift,
            // some on live calls.
            //
            // On a transport failure we keep the session and let the next tick
            // retry — the token is almost certainly still valid, we just could
            // not ask. Only a real rejection ends the session.
            if (supabaseAuthService.lastRefreshWasTransportFailure()) {
              console.warn(
                '[SessionValidator] Token refresh could not reach the auth server — keeping session, will retry.',
              );
              return;
            }

            if (mounted && !hasTriggeredLogout.current) {
              hasTriggeredLogout.current = true;
              await clearSessionCompletely();
              router.push('/auth/sign-in?reason=session_expired');
            }
            return;
          }
        }
      }

      // Store the access token for sendBeacon on tab close
      accessTokenRef.current = session.access_token;

      // If this is a page refresh, the beforeunload beacon may have briefly set us offline.
      // Restore status immediately so the user doesn't appear offline during a refresh.
      if (freshSessionCheck === 'same_tab') {
        const beaconSent = localStorage.getItem(SESSION_OFFLINE_BEACON_KEY);
        if (beaconSent) {
          localStorage.removeItem(SESSION_OFFLINE_BEACON_KEY);
          // Restore to 'available' — useAutoStatusManager will correct to the right status on mount
          try {
            const statusUrl = getApiUrl('users/status');
            fetch(`${statusUrl}?token=${session.access_token}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'available' }),
            }).catch(() => {}); // Best effort — don't block mount
          } catch {
            // Ignore
          }
        }
      }

      // Session is valid - set up this tab as the active tab
      // IMPORTANT: On refresh ('same_tab'), keep the existing tab ID to maintain session continuity
      let activeTabId: string;
      if (freshSessionCheck === 'same_tab') {
        // Use the existing tab ID from sessionStorage (was set in isFreshBrowserSession)
        activeTabId = sessionStorage.getItem(SESSION_TAB_ID_KEY) || generateTabId();
      } else {
        // First login - generate new tab ID
        activeTabId = generateTabId();
      }
      tabIdRef.current = activeTabId;
      sessionStorage.setItem(SESSION_TAB_ID_KEY, activeTabId);
      // Claim heartbeat ownership — a later claim (another tab's "use here instead")
      // beats this one and makes this tab stand down via the storage listener
      claimedAtRef.current = Date.now();

      // Mark that a session is now active (this persists even when heartbeat is cleared)
      localStorage.setItem(SESSION_ACTIVE_FLAG_KEY, 'true');

      // Start heartbeat to track this tab is alive
      // Includes visibility state so we can distinguish "tab hidden" from "tab closed"
      const updateHeartbeat = () => {
        localStorage.setItem(SESSION_HEARTBEAT_KEY, JSON.stringify({
          tabId: tabIdRef.current,
          timestamp: Date.now(),
          claimedAt: claimedAtRef.current,
          state: document.visibilityState === 'visible' ? 'visible' : 'hidden',
        }));
      };

      // When tab becomes hidden (user switches to carrier site, etc.), immediately write
      // a "hidden" heartbeat so other tabs know this tab is backgrounded, not closed
      const handleHeartbeatVisibility = () => {
        updateHeartbeat(); // Write current state immediately on visibility change
      };
      document.addEventListener('visibilitychange', handleHeartbeatVisibility);

      // Initial heartbeat
      updateHeartbeat();

      // Update heartbeat every 2 seconds (frequent enough to detect stale sessions)
      // Browsers throttle this to ~1x/min when hidden, but that's fine —
      // our stale threshold is 5 minutes for hidden tabs
      heartbeatIntervalRef.current = setInterval(updateHeartbeat, 2000);

      visibilityCleanupRef.current = handleHeartbeatVisibility;

      // Session exists and is not expired - mark as validated
      lastValidationRef.current = Date.now();
      setIsValidating(false);
    };

    validateOnMount();

    return () => {
      mounted = false;
      // Clear heartbeat interval on unmount
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (visibilityCleanupRef.current) {
        document.removeEventListener('visibilitychange', visibilityCleanupRef.current);
      }
    };
  }, [router, validateSessionWithBackend, isFreshBrowserSession, generateTabId, clearSessionCompletely]);

  // Set user offline on tab close via sendBeacon
  // beforeunload fires on both refresh and close — we handle the refresh case on mount
  // by detecting 'same_tab' and immediately restoring status
  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = accessTokenRef.current;
      if (!token) return;

      // Mark that we sent an offline beacon (so refresh can restore)
      localStorage.setItem(SESSION_OFFLINE_BEACON_KEY, Date.now().toString());

      // Fire-and-forget beacon to set status offline
      try {
        const statusUrl = getApiUrl('users/status');
        const url = `${statusUrl}?token=${token}`;
        const body = new Blob(
          [JSON.stringify({ status: 'offline' })],
          { type: 'application/json' }
        );
        navigator.sendBeacon(url, body);
      } catch {
        // sendBeacon not supported or failed — best effort
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Handle tab close / browser close detection
  // We ONLY rely on heartbeat staleness (>5 seconds old) to detect tab close
  // We do NOT clear heartbeat on any page events because:
  // - beforeunload fires on refresh
  // - pagehide fires on refresh in some browsers
  // - visibilitychange is unreliable
  // The heartbeat will naturally become stale when the tab is actually closed
  // because the interval stops running

  // Listen for storage changes (detect if another tab tries to take over)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // A duplicate tab just got blocked — rib the agent on the main tab with a
      // center-screen callout. (storage events never fire in the tab that wrote
      // the value, so this only ever shows on the OTHER, active tab)
      if (event.key === SESSION_DUPLICATE_ATTEMPT_KEY && event.newValue) {
        const roast = DUPLICATE_TAB_ROASTS[Math.floor(Math.random() * DUPLICATE_TAB_ROASTS.length)];
        setDuplicateRoast(roast);
        if (roastTimeoutRef.current) clearTimeout(roastTimeoutRef.current);
        roastTimeoutRef.current = setTimeout(() => setDuplicateRoast(null), 6000);
        return;
      }

      if (event.key !== SESSION_HEARTBEAT_KEY) return;

      // If heartbeat was cleared (logout from another context)
      if (event.newValue === null) {
        if (!hasTriggeredLogout.current) {
          hasTriggeredLogout.current = true;
          router.push('/auth/sign-in?reason=logged_out');
        }
        return;
      }

      // Another tab wrote the heartbeat. SINGLE-TAB POLICY:
      // - Duplicate tabs are blocked at mount and never write heartbeats, so a
      //   foreign tabId here normally means a "use here instead" takeover.
      // - Newer claimedAt wins: stand down (blocker UI) WITHOUT touching the shared
      //   session — signing out here would kill the tab the user moved to.
      if (event.newValue && tabIdRef.current) {
        try {
          const newHeartbeat = JSON.parse(event.newValue);
          if (newHeartbeat.tabId !== tabIdRef.current) {
            if (
              typeof newHeartbeat.claimedAt === 'number' &&
              newHeartbeat.claimedAt > claimedAtRef.current
            ) {
              // We lost ownership — stop heartbeating and block this tab
              if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
                heartbeatIntervalRef.current = null;
              }
              sessionStorage.removeItem(SESSION_TAB_ID_KEY);
              // Try to close this abandoned tab; blocker is the fallback when the
              // browser refuses (user-opened tabs can't be script-closed)
              try { window.close(); } catch {}
              setShowDuplicateTabBlocker(true);
              return;
            }
            // Foreign heartbeat WITHOUT a newer claim (legacy format) — keep ours alive
            localStorage.setItem(SESSION_HEARTBEAT_KEY, JSON.stringify({
              tabId: tabIdRef.current,
              timestamp: Date.now(),
              claimedAt: claimedAtRef.current,
              state: document.visibilityState,
            }));
          }
        } catch {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [router]);

  // Validate on visibility change (when user returns to tab after being away)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (hasTriggeredLogout.current) return;

      // NEVER force session validation redirect while on an active call
      // The call will keep working even if the session token is temporarily stale
      // and we'll re-validate once the call ends
      if (isUserOnActiveCall()) return;

      // Only validate if more than 5 minutes since last validation
      const fiveMinutes = 5 * 60 * 1000;
      if (Date.now() - lastValidationRef.current < fiveMinutes) return;

      const session = await supabaseAuthService.getCurrentSession();

      if (!session) {
        if (!hasTriggeredLogout.current) {
          hasTriggeredLogout.current = true;
          await clearSessionCompletely();
          router.push('/auth/sign-in?reason=session_expired');
        }
        return;
      }

      // Check expiry
      if (session.expires_at) {
        const expiresAt = session.expires_at * 1000;
        const now = Date.now();

        if (now >= expiresAt) {
          const refreshedSession = await supabaseAuthService.refreshSession();

          if (!refreshedSession) {
            // Same rule as the other refresh path above: an unreachable auth
            // server is not a dead session. Keep it and retry on the next tick.
            if (supabaseAuthService.lastRefreshWasTransportFailure()) {
              console.warn(
                '[SessionValidator] Token refresh could not reach the auth server — keeping session, will retry.',
              );
              return;
            }

            if (!hasTriggeredLogout.current) {
              hasTriggeredLogout.current = true;
              await clearSessionCompletely();
              router.push('/auth/sign-in?reason=session_expired');
            }
            return;
          }
        }
      }

      // Backend eviction check: was this session replaced by a login elsewhere?
      // (fallback for a missed realtime broadcast)
      const stillValid = await validateSessionWithBackend();
      if (!stillValid) {
        handleSessionInvalidationRef.current(
          'You have been logged out because you logged in from another device.',
        );
        return;
      }

      lastValidationRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [router, clearSessionCompletely, validateSessionWithBackend]);

  // Periodic eviction poll (every 60s) — catches a replaced session even when the
  // realtime broadcast was missed AND the user never switches tabs. Skipped while
  // on an active call, while blocked as a duplicate tab, or after logout triggered.
  useEffect(() => {
    const interval = setInterval(async () => {
      if (hasTriggeredLogout.current) return;
      if (isUserOnActiveCall()) return;

      const stillValid = await validateSessionWithBackend();
      if (!stillValid) {
        handleSessionInvalidationRef.current(
          'You have been logged out because you logged in from another device.',
        );
      }
      // Stays at 60s ON PURPOSE. This is the FALLBACK, not the detection path:
      // eviction is pushed instantly over the `session_events:${userId}` realtime
      // channel (backend SessionEvictionListener broadcasts once per real login).
      // This poll only has to catch a MISSED broadcast — sleeping laptop,
      // backgrounded tab, dropped socket — so polling harder just adds load
      // (every call runs the JWT guard: user enrichment + session check +
      // a tracking transaction) without making the common case any faster.
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [validateSessionWithBackend]);

  // Realtime session listener setup
  useEffect(() => {
    let mounted = true;

    const setupRealtimeListener = async () => {
      // Get current user ID to subscribe to their session channel
      const session = await supabaseAuthService.getCurrentSession();
      if (!session || !mounted) return;

      const userId = session.user?.id;
      if (!userId) {
        return;
      }

      // Store the current session ID so we can check it against broadcasts
      // This prevents the "double login" bug where the new session logs itself out
      // The session_id is stored in the JWT access token as a claim
      let supabaseSessionId: string | null = null;
      if (session.access_token) {
        try {
          // Decode the JWT to extract the session_id claim (base64 decode the payload)
          const parts = session.access_token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            supabaseSessionId = payload.session_id || null;
          }
        } catch (e) {
          // Failed to decode session ID from JWT
        }
      }
      currentSessionIdRef.current = supabaseSessionId;

      try {
        const supabase = await getSupabaseClient();
        const channelName = `session_events:${userId}`;

        // Subscribe to session events for this user (same pattern as IncomingCallContext)
        const channel = supabase
          .channel(channelName)
          .on('broadcast', { event: 'session_invalidated' }, (payload: any) => {
            // Check if this broadcast is meant for us or for old sessions
            // If the broadcast includes our session ID as the "newSessionId", ignore it
            // This means WE are the new session that triggered the invalidation
            const newSessionId = payload.payload?.newSessionId;
            const mySessionId = currentSessionIdRef.current;

            if (newSessionId && mySessionId && newSessionId === mySessionId) {
              return;
            }

            if (mounted && !hasTriggeredLogout.current) {
              const message = payload.payload?.message || 'You have been logged out because you logged in from another device.';
              handleSessionInvalidationRef.current(message);
            }
          })
          .subscribe();

        supabaseChannelRef.current = channel;
      } catch (error) {
        console.error('[SessionValidator] Failed to setup realtime listener:', error);
      }
    };

    setupRealtimeListener();

    // Cleanup on unmount
    return () => {
      mounted = false;
      if (supabaseChannelRef.current) {
        removeRealtimeChannel(supabaseChannelRef.current);
        supabaseChannelRef.current = null;
      }
    };
  }, []);

  // SINGLE-TAB POLICY blocker: this tab is a duplicate — the session is running in
  // another tab. Full-screen, not dismissable; "Use here instead" hands ownership over.
  if (showDuplicateTabBlocker && !showKickedOutModal) {
    return (
      // Fully opaque backdrop — no app content (lead data etc.) visible behind the lock
      <div className="fixed inset-0 bg-gray-950 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Already open in another tab</h2>
          </div>
          <p className="text-gray-600">
            Only one tab can be open at a time. Please close this tab.
          </p>
        </div>
      </div>
    );
  }

  // Show kicked out modal
  if (showKickedOutModal) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Session Ended</h2>
          </div>
          <p className="text-gray-600 mb-4">
            {kickedOutMessage}
          </p>
          <p className="text-sm text-gray-500">
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  // Center-screen roast on the main tab when a duplicate tab gets blocked.
  // Doesn't block the page (pointer-events pass through around the card);
  // click the card to dismiss early, otherwise it self-dismisses in 6s.
  if (duplicateRoast) {
    return (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none">
        <div
          onClick={() => {
            if (roastTimeoutRef.current) clearTimeout(roastTimeoutRef.current);
            setDuplicateRoast(null);
          }}
          className="pointer-events-auto cursor-pointer bg-white rounded-xl px-8 py-6 max-w-md mx-4 shadow-2xl border border-gray-200 animate-in zoom-in-75 fade-in duration-300"
        >
          <p className="text-lg font-semibold text-gray-900 text-center leading-relaxed">
            {duplicateRoast}
          </p>
          <p className="text-xs text-gray-400 text-center mt-3">
            (click to dismiss)
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default SessionValidator;
